import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Button,
  Grid,
  Avatar,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import {
  Person as PersonIcon,
  Message as MessageIcon,
  TrendingFlat as TrendingFlatIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';

interface StepAgentSettingsProps {
  formData: any;
  onComplete: (data: any) => void;
  onNext: () => void;
  onBack?: () => void;
  isActive: boolean;
}

const agentSettingsSchema = yup.object({
  agent_name: yup.string().default('Assistant FAQ'),
  welcome_message: yup.string().default('Bienvenue sur notre service FAQ WhatsApp.'),
  escalation_message: yup.string().default('Je vous mets en relation avec un conseiller.'),
});

export const StepAgentSettings: React.FC<StepAgentSettingsProps> = ({
  formData,
  onComplete,
  onNext,
  onBack,
  isActive,
}) => {
  const formik = useFormik({
    initialValues: {
      agent_name: formData?.agent_name || 'Assistant FAQ',
      welcome_message: formData?.welcome_message || 'Bienvenue sur notre service FAQ WhatsApp.',
      escalation_message: formData?.escalation_message || 'Je vous mets en relation avec un conseiller.',
    },
    // Keep the edit form in sync with the asynchronously loaded configuration.
    enableReinitialize: true,
    validationSchema: agentSettingsSchema,
    onSubmit: (values) => {
      onComplete(values);
    },
  });

  if (!isActive) return null;

  return (
    <Box sx={{ py: 2 }}>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert severity="info">
              Cette étape est optionnelle. Vous pouvez garder les messages par défaut et les ajuster
              plus tard depuis la page de configuration.
            </Alert>
          </Grid>

          {/* Agent Name */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nom de l’agent FAQ"
              name="agent_name"
              value={formik.values.agent_name}
              onChange={formik.handleChange}
              error={formik.touched.agent_name && Boolean(formik.errors.agent_name)}
              helperText={formik.touched.agent_name && formik.errors.agent_name}
              placeholder="Ex: Agent Virtuel"
              InputProps={{
                startAdornment: (
                  <PersonIcon color="action" />
                ),
              }}
            />
          </Grid>

          {/* Welcome Message */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Message de bienvenue"
              name="welcome_message"
              value={formik.values.welcome_message}
              onChange={formik.handleChange}
              error={formik.touched.welcome_message && Boolean(formik.errors.welcome_message)}
              helperText={formik.touched.welcome_message && formik.errors.welcome_message}
              placeholder="Ex: Bienvenue sur notre service FAQ !"
              InputProps={{
                startAdornment: (
                  <MessageIcon color="action" />
                ),
              }}
            />
          </Grid>

          {/* Escalation Message */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Message d’escalade"
              name="escalation_message"
              value={formik.values.escalation_message}
              onChange={formik.handleChange}
              error={formik.touched.escalation_message && Boolean(formik.errors.escalation_message)}
              helperText={formik.touched.escalation_message && formik.errors.escalation_message}
              placeholder="Ex: Je vous mets en relation avec un conseiller."
              InputProps={{
                startAdornment: (
                  <TrendingFlatIcon color="action" />
                ),
              }}
            />
          </Grid>

          {/* Avatar Preview */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Avatar sx={{ width: 72, height: 72, mx: 'auto', mb: 2 }}>
                  <PersonIcon fontSize="large" />
                </Avatar>
                <Typography variant="h6">{formik.values.agent_name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Aperçu de l’avatar de votre agent
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Actions */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              {onBack && (
                <Button onClick={onBack}>
                  Retour
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                sx={{ minWidth: 120 }}
              >
                Continuer
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};
